// eslint-disable-next-line import/no-webpack-loader-syntax
import MainWorker from "worker-loader?name=static/js/[hash].worker.js!./main.worker"
import { JobSubmitData } from "../../../Models/Client/JobSubmitData"

export type workerRef = "CSV_WORKER" | "ZIP_DATA_WORKER"

export type SpecialWorker = typeof Worker & (new () => Worker)

export type CallWorker<T = workerRef> = (
    key: T,
    data: WorkerInput<T>,
    attempts?: number
) => Promise<WorkerReturn<T>>

export interface CSVWorkerArgs {
    file: File
    schema: any
    uploadSizeThreshold: number
    maxReportErrorItemCount: number
    single: boolean
}

export interface ZipDataWorkerArgs {
    data: { name: string; url: string }[]
}

export interface CSVWorkerReturn {
    isJSONFile: boolean
    data: JobSubmitData
    messages: string[]
}

export type ZipDataWorkerReturn = Blob

export type WorkerInput<T = workerRef> = T extends "CSV_WORKER"
    ? CSVWorkerArgs
    : ZipDataWorkerArgs

export type WorkerReturn<T = workerRef> = T extends "CSV_WORKER"
    ? CSVWorkerReturn
    : ZipDataWorkerReturn

export interface WorkerPool<T = workerRef> {
    call: CallWorker<T>
    deactivate: () => void
    active: boolean
}

export enum PoolWorkerState {
    ready,
    unavailable,
    dead
}

export interface PoolWorker<T = workerRef> {
    run: CallWorker<T>
    kill: () => void
    state: PoolWorkerState
}

interface WorkerTask<T = workerRef> {
    key: T
    data: WorkerInput<T>
    resolver: (result: WorkerReturn<T>) => void
    rejector: (error: unknown) => void
}

export function createWorkerPool<T = workerRef>(
    maxWorkers: number = 3
): WorkerPool<T> {
    const workers: PoolWorker<T>[] = []
    const queue: WorkerTask<T>[] = []
    let active = true

    function createWorker(): PoolWorker<T> {
        const worker = new MainWorker()
        const poolWorker: PoolWorker<T> = {
            state: PoolWorkerState.ready,
            run: (key, data) => {
                return new Promise<WorkerReturn<T>>((resolve, reject) => {
                    poolWorker.state = PoolWorkerState.unavailable
                    const listener = (event: MessageEvent) => {
                        if ("data" in event.data) {
                            resolve(event.data.data)
                        } else {
                            reject(event.data.error)
                        }
                        worker.removeEventListener("message", listener)
                        poolWorker.state = PoolWorkerState.ready
                    }
                    worker.addEventListener("message", listener)
                    worker.postMessage({ worker: key, args: data })
                })
            },
            kill: () => {
                worker.terminate()
                poolWorker.state = PoolWorkerState.dead
            }
        }

        return poolWorker
    }

    function getOrCreateWorker(): PoolWorker<T> | undefined {
        const worker = workers.find(w => w.state === PoolWorkerState.ready)

        if (worker !== undefined) {
            return worker
        }

        if (workers.length < maxWorkers) {
            const newWorker = createWorker()

            workers.push(newWorker)

            return newWorker
        }
    }

    const call: CallWorker<T> = async (key, data): Promise<WorkerReturn<T>> => {
        const promise = new Promise<WorkerReturn<T>>((resolver, rejector) =>
            queue.push({ key, data, resolver, rejector })
        )

        processQueue()

        return promise
    }

    function deactivate() {
        if (!active) throw Error("Worker pool not active")
        const livingWorkers = workers.filter(
            w => w.state !== PoolWorkerState.dead
        )
        livingWorkers.forEach(w => w.kill())
        active = false
    }

    async function processQueue() {
        const item = queue.shift()

        if (item === undefined) {
            return
        }

        const worker = getOrCreateWorker()

        if (worker === undefined) {
            queue.unshift(item)
            return
        }

        await worker
            .run(item.key, item.data)
            .then(item.resolver)
            .catch(item.rejector)
        processQueue()
    }

    return { call, deactivate, active }
}
